// @flow

import { noop } from "lodash";

const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';
const CLEAR_ALL_NOTIFICATIONS = 'CLEAR_ALL_NOTIFICATIONS';
const REMOVE_PRIVATE_NOTIFICATIONS = 'REMOVE_PRIVATE_NOTIFICATIONS';
// Keep SET_NOTIFICATION for backward compatibility
const SET_NOTIFICATION = 'SET_NOTIFICATION';

type Props = {
  message?: any,
  isError?: boolean,
  duration?: number,
  dispatchClass?: string,
  hasButton?: boolean,
  buttonAction?: Function,
  buttonLabel?: string,
  buttonVariant?: string,
  Avatar?: any,
  isWelcome?: boolean,
  userId?: string,
  userName?: string,
  photoUrl?: string,
  isSticky?: boolean,
  dedupeKey?: string,
  isPrivateNotification?: boolean,
};

let notificationIdCounter = 0;

export function addFlashNotification({
  message = '',
  userName,
  userId,
  isError = false,
  duration = 4000,
  dispatchClass = '',
  hasButton,
  buttonAction,
  buttonLabel,
  buttonVariant = 'primary',
  Avatar,
  isWelcome,
  photoUrl = '',
  isSticky = false,
  dedupeKey = null,
  isPrivateNotification = false,
}: Props): any {
  return {
    type: ADD_NOTIFICATION,
    payload: {
      id: `notification_${Date.now()}_${++notificationIdCounter}`,
      message,
      isError,
      duration,
      dispatchClass,
      hasButton,
      buttonAction,
      buttonLabel,
      buttonVariant,
      Avatar,
      isWelcome,
      userId,
      userName,
      photoUrl,
      isSticky,
      dedupeKey,
      isPrivateNotification,
    },
  };
}

export function removeFlashNotification(id: string): any {
  return {
    type: REMOVE_NOTIFICATION,
    payload: { id },
  };
}

export function clearAllNotifications(): any {
  return {
    type: CLEAR_ALL_NOTIFICATIONS,
  };
}

export function removePrivateNotifications(): any {
  return {
    type: REMOVE_PRIVATE_NOTIFICATIONS,
  };
}

// Backward compatibility: setFlashNotification now adds to queue
export function setFlashNotification({
  message = '',
  userName,
  userId,
  isError = false,
  duration = 4000,
  dispatchClass = '',
  hasButton,
  buttonAction,
  buttonLabel,
  buttonVariant = 'primary',
  Avatar,
  isWelcome,
  photoUrl = '',
  isSticky = false,
  dedupeKey = null,
  isPrivateNotification = false,
}: Props): any {
  // If message is empty, clear all notifications (old behavior for clearing)
  if (!message) {
    return clearAllNotifications();
  }

  // Otherwise, add notification to queue
  return addFlashNotification({
    message,
    userName,
    userId,
    isError,
    duration,
    dispatchClass,
    hasButton,
    buttonAction,
    buttonLabel,
    buttonVariant,
    Avatar,
    isWelcome,
    photoUrl,
    isSticky,
    dedupeKey,
    isPrivateNotification,
  });
}

const initialState = {
  notifications: [],
  // Keep legacy single notification for backward compatibility
  message: '',
  isError: false,
  duration: 4000,
  dispatchClass: '',
  hasButton: false,
  buttonAction: noop,
  buttonLabel: '',
  buttonVariant: 'primary',
  Avatar: undefined,
  isWelcome: false,
  userId: '',
  userName: '',
  photoUrl: '',
  isSticky: false,
  isPrivateNotification: false,
};

const withRemainingNotifications = (
  state: Object,
  notifications: Array<Object>,
): Object => ({
  ...state,
  ...(notifications[0] || initialState),
  notifications,
});

export default function flashNotification(
  state: Object = initialState,
  action: Object,
): any {
  switch (action.type) {
    case ADD_NOTIFICATION: {
      const { dedupeKey } = action.payload;

      if (
        dedupeKey &&
        state.notifications.some(n => n.dedupeKey === dedupeKey)
      ) {
        return state;
      }

      return {
        ...state,
        notifications: [...state.notifications, action.payload],
        ...action.payload,
      };
    }

    case REMOVE_NOTIFICATION:
      return withRemainingNotifications(
        state,
        state.notifications.filter(notif => notif.id !== action.payload.id),
      );
    case REMOVE_PRIVATE_NOTIFICATIONS:
      return withRemainingNotifications(
        state,
        state.notifications.filter(
          notif => !notif.isPrivateNotification && !notif.isSticky,
        ),
      );
    case CLEAR_ALL_NOTIFICATIONS:
      return {
        ...initialState,
        notifications: [],
      };
    case SET_NOTIFICATION:
      // Legacy behavior: if message is empty, clear
      if (!action.message) {
        return {
          ...initialState,
          notifications: [],
        };
      }

      // Otherwise treat as add
      // eslint-disable-next-line no-case-declarations
      const newNotification = {
        id: `notification_${Date.now()}_${++notificationIdCounter}`,
        message: action.message,
        isError: action.isError,
        duration: action.duration,
        dispatchClass: action.dispatchClass,
        hasButton: action.hasButton,
        buttonAction: action.buttonAction,
        buttonLabel: action.buttonLabel,
        buttonVariant: action.buttonVariant,
        Avatar: action.Avatar,
        isWelcome: action.isWelcome,
        userId: action.userId,
        userName: action.userName,
        photoUrl: action.photoUrl,
        isSticky: action.isSticky,
        dedupeKey: action.dedupeKey || null,
        isPrivateNotification: !!action.isPrivateNotification,
      };

      if (
        newNotification.dedupeKey &&
        state.notifications.some(n => n.dedupeKey === newNotification.dedupeKey)
      ) {
        return state;
      }

      return {
        ...state,
        notifications: [...state.notifications, newNotification],
        ...newNotification,
      };
    default:
      return state;
  }
}
